import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Filter } from "lucide-react";
import { useState, useEffect } from "react";
import { judgmentsReadJudgments } from "@/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SearchFiltersState {
  category: string;
  instances: string[];
  courts: string[];
  yearFrom: number;
  yearTo: number;
  caseNumbers: string[];
}

interface SearchFiltersProps {
  filters: SearchFiltersState;
  onFiltersChange: (filters: SearchFiltersState) => void;
}

export function SearchFilters({ filters, onFiltersChange }: SearchFiltersProps) {
  const [availableCourts, setAvailableCourts] = useState<string[]>([]);
  const [availableCaseNumbers, setAvailableCaseNumbers] = useState<string[]>([]);
  const [caseNumberSearch, setCaseNumberSearch] = useState("");
  const [courtsOpen, setCourtsOpen] = useState(false);
  const [caseNumbersOpen, setCaseNumbersOpen] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1998 }, (_, i) => currentYear - i);

  // Fetch available courts and case numbers
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await judgmentsReadJudgments({
          limit: 1000,
          onlyApproved: true,
        });

        const courts = new Set<string>();
        const caseNumbers = new Set<string>();

        response.data.forEach((judgment) => {
          if (judgment.classification?.domstol) courts.add(judgment.classification.domstol);
          if (judgment.classification?.målnummer) caseNumbers.add(judgment.classification.målnummer);
        });

        setAvailableCourts(Array.from(courts).sort());
        setAvailableCaseNumbers(Array.from(caseNumbers).sort());
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
      }
    };

    fetchOptions();
  }, []);

  const toggleInstance = (instance: string) => {
    const newInstances = filters.instances.includes(instance)
      ? filters.instances.filter((i) => i !== instance)
      : [...filters.instances, instance];
    onFiltersChange({ ...filters, instances: newInstances });
  };

  const toggleCourt = (court: string) => {
    const newCourts = filters.courts.includes(court)
      ? filters.courts.filter((c) => c !== court)
      : [...filters.courts, court];
    onFiltersChange({ ...filters, courts: newCourts });
  };

  const toggleCaseNumber = (caseNumber: string) => {
    const newCaseNumbers = filters.caseNumbers.includes(caseNumber)
      ? filters.caseNumbers.filter((c) => c !== caseNumber)
      : [...filters.caseNumbers, caseNumber];
    onFiltersChange({ ...filters, caseNumbers: newCaseNumbers });
  };

  const removeCourt = (court: string) => {
    onFiltersChange({
      ...filters,
      courts: filters.courts.filter((c) => c !== court),
    });
  };

  const removeCaseNumber = (caseNumber: string) => {
    onFiltersChange({
      ...filters,
      caseNumbers: filters.caseNumbers.filter((c) => c !== caseNumber),
    });
  };

  const filteredCaseNumbers = availableCaseNumbers.filter((num) =>
    num.toLowerCase().includes(caseNumberSearch.toLowerCase())
  );

  return (
    <Card className="mb-4">
      <CardHeader className="p-3 pb-2">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <CardTitle className="text-sm font-medium">Vad vill du prata om?</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0 space-y-3">
        {/* Category */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Kategori</Label>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="cat-entreprenad"
                checked={filters.category === "Entreprenadrätt"}
                onCheckedChange={(checked) =>
                  onFiltersChange({
                    ...filters,
                    category: checked ? "Entreprenadrätt" : "",
                  })
                }
              />
              <Label
                htmlFor="cat-entreprenad"
                className="text-xs font-normal cursor-pointer"
              >
                Entreprenadrätt
              </Label>
            </div>
            <div className="flex items-center space-x-2 opacity-50">
              <Checkbox id="cat-hyres" disabled />
              <Label htmlFor="cat-hyres" className="text-xs font-normal">
                Hyresrätt <span className="text-muted-foreground">(kommer snart)</span>
              </Label>
            </div>
          </div>
        </div>

        {/* Instance */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Instans</Label>
          <div className="space-y-1">
            {["HD", "Hovrätt", "Tingsrätt"].map((instance) => (
              <div key={instance} className="flex items-center space-x-2">
                <Checkbox
                  id={`instance-${instance}`}
                  checked={filters.instances.includes(instance)}
                  onCheckedChange={() => toggleInstance(instance)}
                />
                <Label
                  htmlFor={`instance-${instance}`}
                  className="text-xs font-normal cursor-pointer"
                >
                  {instance}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Courts */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Domstol</Label>
          <Popover open={courtsOpen} onOpenChange={setCourtsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={courtsOpen}
                className="w-full justify-between h-8 text-xs"
              >
                {filters.courts.length > 0
                  ? `${filters.courts.length} valda`
                  : "Välj domstolar..."}
                <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Sök domstol..." className="h-8 text-xs" />
                <CommandList>
                  <CommandEmpty className="text-xs p-2">Ingen domstol hittades.</CommandEmpty>
                  <CommandGroup>
                    {availableCourts.map((court) => (
                      <CommandItem
                        key={court}
                        value={court}
                        onSelect={() => {
                          toggleCourt(court);
                        }}
                        className="text-xs"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-3 w-3",
                            filters.courts.includes(court) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {court}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {filters.courts.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {filters.courts.map((court) => (
                <Badge
                  key={court}
                  variant="secondary"
                  className="text-xs px-2 py-0 h-5"
                >
                  {court}
                  <button
                    onClick={() => removeCourt(court)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Years */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Tidpunkt för domslut</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground mb-1">Från år</Label>
              <Select
                value={filters.yearFrom.toString()}
                onValueChange={(value) =>
                  onFiltersChange({ ...filters, yearFrom: parseInt(value) })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.reverse().map((year) => (
                    <SelectItem key={year} value={year.toString()} className="text-xs">
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1">Till år</Label>
              <Select
                value={filters.yearTo.toString()}
                onValueChange={(value) =>
                  onFiltersChange({ ...filters, yearTo: parseInt(value) })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.reverse().map((year) => (
                    <SelectItem key={year} value={year.toString()} className="text-xs">
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Case Numbers */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Målnummer</Label>
          <Popover open={caseNumbersOpen} onOpenChange={setCaseNumbersOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={caseNumbersOpen}
                className="w-full justify-between h-8 text-xs"
              >
                {filters.caseNumbers.length > 0
                  ? `${filters.caseNumbers.length} valda`
                  : "Välj målnummer..."}
                <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder="Sök målnummer..."
                  className="h-8 text-xs"
                  value={caseNumberSearch}
                  onValueChange={setCaseNumberSearch}
                />
                <CommandList>
                  <CommandEmpty className="text-xs p-2">Inget målnummer hittades.</CommandEmpty>
                  <CommandGroup>
                    {filteredCaseNumbers.slice(0, 50).map((caseNumber) => (
                      <CommandItem
                        key={caseNumber}
                        value={caseNumber}
                        onSelect={() => {
                          toggleCaseNumber(caseNumber);
                        }}
                        className="text-xs"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-3 w-3",
                            filters.caseNumbers.includes(caseNumber)
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        {caseNumber}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {filters.caseNumbers.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {filters.caseNumbers.map((caseNumber) => (
                <Badge
                  key={caseNumber}
                  variant="secondary"
                  className="text-xs px-2 py-0 h-5"
                >
                  {caseNumber}
                  <button
                    onClick={() => removeCaseNumber(caseNumber)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
